/**
 * Utility functions for calculating item completion percentage
 * 
 * Priority System (3-tier):
 * 1. Manual Override - User input (highest priority)
 * 2. Milestone-based - Weighted average by duration
 * 3. Time-based - Fallback calculation based on dates
 */

import { TimelineItem, ItemMilestone } from '@/types/item';
import { formatDateForBackend } from '@/utils/dateFormatUtils';

/**
 * Calculate number of days between two ISO date strings
 */
export const getDaysBetween = (startDate: string, endDate: string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = end.getTime() - start.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(1, diffDays); // Minimum 1 day
};

/**
 * Calculate time-based completion percentage
 * Formula: (days elapsed / total days) * 100
 */
export const calculateTimeBasedCompletion = (startDate: string, endDate: string): number => {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // If not started yet
  if (now < start) return 0;
  
  // If already completed
  if (now > end) return 100;
  
  const totalDays = getDaysBetween(startDate, endDate);
  const elapsedDays = getDaysBetween(startDate, formatDateForBackend(now)!);
  
  const percentage = (elapsedDays / totalDays) * 100;
  return Math.min(100, Math.max(0, Math.round(percentage)));
};

/**
 * Calculate milestone-based completion percentage
 * Formula: Weighted average by milestone duration
 */
export const calculateMilestoneBasedCompletion = (milestones: ItemMilestone[]): number => {
  if (!milestones || milestones.length === 0) return 0;
  
  // Calculate total weight (sum of all milestone durations)
  const totalWeight = milestones.reduce((sum, milestone) => {
    const duration = getDaysBetween(milestone.start_date, milestone.end_date);
    return sum + duration;
  }, 0);
  
  if (totalWeight === 0) return 0;
  
  // Calculate weighted sum
  const weightedSum = milestones.reduce((sum, milestone) => {
    const duration = getDaysBetween(milestone.start_date, milestone.end_date);
    const weight = duration / totalWeight;
    return sum + (milestone.completion_percentage * weight);
  }, 0);
  
  return Math.round(weightedSum);
};

/**
 * Main function: Calculate item completion percentage
 * 
 * Priority:
 * 1. Manual override (if set)
 * 2. Milestone-based (if milestones exist)
 * 3. Time-based (fallback)
 */
export const calculateItemCompletion = (
  item: TimelineItem, 
  milestones: ItemMilestone[] = [],
  manualOverride?: number | null
): number => {
  // Priority 1: Manual override
  if (manualOverride !== null && manualOverride !== undefined) {
    return manualOverride;
  }
  
  // Priority 2: Milestone-based (if milestones exist)
  if (milestones && milestones.length > 0) {
    return calculateMilestoneBasedCompletion(milestones);
  }
  
  // Priority 3: Time-based (fallback)
  return calculateTimeBasedCompletion(item.startDate, item.endDate);
};
